import {
  ExportPicker,
  Parameters,
  Table,
  TablePagination,
} from '@/components/controls';
import { useAggregate, useDocumentTitle } from '@/hooks';
import {
  getPersonPolls,
  isEmpty,
  radioPollHeaders,
  shortPersonHeaders,
} from '@/utils';
import { events, rowsPerPageOptions } from '@/utils/config';
import { PlayArrow as PlayArrowIcon } from '@mui/icons-material';
import {
  Box,
  IconButton,
  Paper,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';
import { atom, useAtom } from 'jotai';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { downloadPipelineFn, headers, pipelineFn } from './utils';

const {
  eventFilters: { personLocationVisits: eventFilters },
} = events;

const csvHeaders = [...shortPersonHeaders, ...radioPollHeaders];

function ReplayLink({ entry }) {
  const navigate = useNavigate();

  const handleViewClick = (identifier) => () => {
    navigate(`/eventreplay/personLocationIntersections/${identifier}`);
  };

  return (
    <Tooltip title="View">
      <IconButton
        size="small"
        aria-label="View"
        onClick={handleViewClick(entry.identifier)}
      >
        <PlayArrowIcon />
      </IconButton>
    </Tooltip>
  );
}

function DownloadPersonPollsLink({ entry }) {
  async function fetchDataToConvert() {
    const polls = await getPersonPolls(
      entry.person.radioSsi,
      entry.startTime,
      entry.endTime,
    );

    const data = polls.map((feature) => ({
      name: [entry.person.forenames, entry.person.surname].join(' '),
      personRole: entry.person.role,
      collarNumber: entry.person.collarNumber,
      ...feature,
      time: new Date(feature.time),
      radioSsi: feature.ssi,
      longitude: feature.position ? feature.position.coordinates[0] : 0,
      latitude: feature.position ? feature.position.coordinates[1] : 0,
    }));

    return data;
  }

  return (
    <ExportPicker
      fetchDataToConvert={fetchDataToConvert}
      filename="Person Location Visit"
      headers={csvHeaders}
    />
  );
}

const replayColumn = {
  label: '',
  key: 'replay',
  type: 'component',
  component: ReplayLink,
};

const downloadColumn = {
  label: '',
  key: 'download',
  type: 'component',
  component: DownloadPersonPollsLink,
};

const stateAtom = atom({
  orderBy: 'startTime',
  order: 'asc',
  page: 0,
  rowsPerPage: rowsPerPageOptions[0],
  query: {},
  parameters: {},
});

export function PersonLocationVisits() {
  useDocumentTitle('IR3 | Person Location Visits');
  const [{ orderBy, order, page, rowsPerPage, query, parameters }, setState] =
    useAtom(stateAtom);
  const collection = 'personLocationIntersections';
  const pipeline = useMemo(
    () => pipelineFn(orderBy, order, page, rowsPerPage, query),
    [orderBy, order, page, rowsPerPage, query],
  );
  const totalsPipeline = [
    { $match: query },
    {
      $group: {
        _id: null,
        total: { $sum: 1 },
        durationSeconds: { $sum: '$durationSeconds' },
      },
    },
    { $unset: '_id' },
  ];
  const { data, isFetching, cancel } = useAggregate(
    collection,
    pipeline,
    !isEmpty(query),
  );
  const {
    data: aggregated,
    isFetching: totalsFetching,
    cancel: cancelTotals,
  } = useAggregate(collection, totalsPipeline, !isEmpty(query), true);
  const { total, ...totals } = aggregated[0] || {};
  const csvColumns = headers.map(({ label, key }) => ({
    header: label,
    key: key,
  }));

  function handleCancel() {
    cancel();
    cancelTotals();
  }

  const handleStateChange = (name) => (value) => {
    setState((state) => ({ ...state, [name]: value }));
  };

  const handleStateChangeWithEvent = (name) => (event, value) => {
    setState((state) => ({ ...state, [name]: value }));
  };

  function handleRowsPerPageChange(event) {
    setState((state) => ({
      ...state,
      rowsPerPage: event.target.value,
      page: 0,
    }));
  }

  return (
    <Box
      sx={{
        display: 'flex',
        height: 'calc(100vh - 48px)',
        overflow: 'hidden',
        backgroundColor: 'background.default',
      }}
    >
      <Parameters
        collection={collection}
        onFetch={handleStateChangeWithEvent('query')}
        onCancel={handleCancel}
        isFetching={isFetching || totalsFetching}
        value={parameters}
        onChange={handleStateChange('parameters')}
        sx={{ mt: 1, width: 264 }}
        person
        location
        eventFilters={eventFilters}
        pipelineFn={downloadPipelineFn}
        columns={csvColumns}
      />
      <Box
        sx={{
          flex: 1,
          height: 'calc(100vh - 48px)',
          overflowY: 'auto',
          overflowX: 'hidden',
        }}
      >
        <Toolbar variant="dense" disableGutters sx={{ p: 1, pb: 0 }}>
          <Typography sx={{ flexGrow: 1 }} variant="subtitle1">
            Person Location Visits
          </Typography>
        </Toolbar>
        <Paper sx={{ m: [0, 1, 1], minWidth: 240 }}>
          <Box sx={isFetching ? { opacity: 0.5 } : undefined}>
            <Table
              styles={{
                tableContainer: {
                  height: 'calc(100vh - 172px)',
                  overflowY: 'scroll',
                },
                table: {
                  minWidth: 750,
                },
              }}
              data={data}
              headers={headers.concat(replayColumn).concat(downloadColumn)}
              rowsPerPage={rowsPerPage}
              page={0}
              order={order}
              orderBy={orderBy}
              onOrderChange={handleStateChange('order')}
              onOrderByChange={handleStateChange('orderBy')}
              totals={totals}
            />
            <TablePagination
              rowsPerPageOptions={rowsPerPageOptions}
              component="div"
              count={total ?? 0}
              rowsPerPage={rowsPerPage}
              page={total ? page : 0}
              onPageChange={handleStateChangeWithEvent('page')}
              onRowsPerPageChange={handleRowsPerPageChange}
            />
          </Box>
        </Paper>
      </Box>
    </Box>
  );
}
